import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getOffersByProductId, getProductBySlug } from '../../utils/api';
import { usePageSeo } from '../../seo/usePageSeo';
import { getDisplayImageSrc } from '../PriceComparison/simpleProductPageShared';
import RetailerLogo from '../RetailerLogo';
import {
  AFFILIATE_PAGE_SOURCE,
  trackComparisonPriceJump,
  trackComparisonStickyPriceClick,
  trackComparisonStickyPriceImpression,
  trackPricedAffiliateDealClick,
  trackPriceSectionView,
  trackPrimaryCtaClick,
  trackRetailerOptionImpression,
} from '../../utils/enhancedAnalytics';
import { filterValidComparisonOffers, getComparableOfferPrice } from './comparisonOffers';
import { getRetailerPriceModule } from './retailerPriceModule';
import AmazonPriceNotice from '../PriceComparison/AmazonPriceNotice';
import {
  STICKY_MOBILE_MEDIA_QUERY,
  STICKY_PRICES_ROOT_MARGIN,
  getStickyPriceBarSummary,
  hasReachedComparisonStart,
  hasReachedRetailerModule,
} from './stickyPriceBar';
import {
  buildUpgradeSpecRows,
  getManufacturerConditionNote,
  getUpgradeComparisonFacts,
  getUpgradeComparisonSourceLinks,
} from './comparisonVerifiedSpecs';
import { getUpgradeEditorial } from './upgradeComparisonEditorial';
import './ProductVsComparisonPage.css';
import './UpgradeComparisonPage.css';

const AFFILIATE_DISCLOSURE =
  'Angler Built may earn a commission when you purchase through retailer links, at no additional cost to you.';

const LVS34_TRANSDUCER_ONLY_SRC =
  '/images/transducers/livescope-plus-lvs34-transducer-only.jpg';



const RANGE_LAYOUT_MOBILE = {
  width: 360,
  height: 168,
  left: 12,
  track: 336,
  tickY: 18,
  axisY: 26,
  ownedLabelY: 48,
  ownedBarY: 54,
  consideredLabelY: 104,
  consideredBarY: 110,
  barH: 28,
  endInset: 10,
};

const RANGE_LAYOUT_DESKTOP = {
  width: 960,
  height: 148,
  left: 28,
  track: 904,
  tickY: 18,
  axisY: 26,
  ownedLabelY: 46,
  ownedBarY: 52,
  consideredLabelY: 96,
  consideredBarY: 102,
  barH: 22,
  endInset: 12,
};

const BEAM_FORWARD_PITCH = 45;
const BEAM_SCENES = {
  forward: {
    mode: 'forward',
    view: 'side-view',
    orientation: 'SIDE VIEW',
    width: 360,
    height: 168,
    cx: 68,
    cy: 40,
    r: 148,
    waterY: 22,
    bedY: 154,
  },
  down: {
    mode: 'down',
    view: 'cross-section',
    orientation: 'CROSS-SECTION',
    width: 360,
    height: 168,
    cx: 180,
    cy: 28,
    r: 128,
    waterY: 18,
    bedY: 152,
  },
  perspective: {
    mode: 'perspective',
    view: 'top-down',
    orientation: 'TOP-DOWN VIEW',
    width: 360,
    height: 168,
    cx: 180,
    cy: 150,
    r: 136,
    waterY: null,
    bedY: null,
  },
};
const BEAM_SPECKLES = {
  forward: [
    [148, 78],
    [196, 104],
    [232, 128],
  ],
  down: [
    [180, 72],
    [156, 108],
    [204, 118],
  ],
  perspective: [
    [180, 78],
    [148, 104],
    [214, 98],
  ],
};

const SEP_SPECKLES = [
  [-0.74, 0.36, 0.85],
  [0.62, -0.4, 0.65],
  [0.14, 0.52, 0.5],
  [-0.3, -0.46, 0.6],
  [0.84, 0.18, 0.45],
  [-0.9, -0.1, 0.4],
  [0.36, 0.04, 0.35],
  [-0.08, 0.62, 0.42],
];

function sepFieldLayout(viewport, spacingIn, ownedThresholdIn) {
  const isDesktop = viewport === 'desktop';
  const width = isDesktop ? 640 : 360;
  const height = isDesktop ? 212 : 188;
  const laneX = isDesktop ? 16 : 10;
  const laneW = isDesktop ? 608 : 340;
  const laneH = isDesktop ? 82 : 72;
  const pad = isDesktop ? 36 : 26;
  const halfExtentIn = spacingIn / 2 + ownedThresholdIn / 2;
  const pxPerIn = (laneW / 2 - pad) / halfExtentIn;
  return {
    width,
    height,
    cx: laneX + laneW / 2,
    ownedY: isDesktop ? 56 : 50,
    consideredY: isDesktop ? 156 : 140,
    laneX,
    laneW,
    laneH,
    pxPerIn,
  };
}

function trackUpgradeAffiliateClick({
  retailer,
  href,
  offer,
  productSlug,
  comparisonId,
  product,
  freshnessState,
}) {
  try {
    trackPricedAffiliateDealClick({
      retailer,
      affiliateUrl: href,
      salePrice: getComparableOfferPrice(offer),
      pageSource: AFFILIATE_PAGE_SOURCE.UPGRADE_COMPARISON,
      productSlug,
      product,
      comparisonId,
      offerId: offer?.id || offer?.offer_id,
      placement: 'current_prices',
      freshnessState,
    });
  } catch {
    // Analytics must never block outbound navigation.
  }
}

function RetailerMark({ retailer, className = 'ab-cmp-retailer-logo' }) {
  return <RetailerLogo name={retailer} className={className} />;
}

function retailerTestId(retailer) {
  return String(retailer || 'retailer')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function RetailerChoice({ row, productSlug, comparisonId, variant, product }) {
  return (
    <a
      className={[
        'ab-upg-rpm-choice',
        `ab-upg-rpm-choice-${variant}`,
        row.isTiedLowest ? 'ab-upg-rpm-choice-tied' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      href={row.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={row.ariaLabel}
      data-testid={`upgrade-rpm-retailer-${retailerTestId(row.retailer)}`}
      data-position={row.position}
      data-freshness={row.freshnessState}
      data-lowest={row.isLowestPrice ? 'true' : 'false'}
      data-tied={row.isTiedLowest ? 'true' : 'false'}
      onClick={() => {
        trackUpgradeAffiliateClick({
          retailer: row.retailer,
          href: row.href,
          offer: row.offer,
          productSlug,
          comparisonId,
          product,
          freshnessState: row.freshnessState,
        });
      }}
    >
      <span className="ab-upg-rpm-choice-main">
        <RetailerMark retailer={row.retailer} />
        {row.showPriceInControl && row.priceLabel && !row.lastKnownPriceLine ? (
          <span className="ab-upg-rpm-choice-price">{row.priceLabel}</span>
        ) : null}
        <span className="ab-upg-rpm-choice-ext" aria-hidden="true">
          ↗
        </span>
      </span>
      {row.showVerifiedInControl && row.verifiedLine ? (
        <span className="ab-upg-rpm-choice-meta">{row.verifiedLine}</span>
      ) : null}
      {row.lastKnownPriceLine ? (
        <span className="ab-upg-rpm-choice-meta">{row.lastKnownPriceLine}</span>
      ) : null}
      {row.lastKnownDisclaimer ? (
        <span className="ab-upg-rpm-choice-note">{row.lastKnownDisclaimer}</span>
      ) : null}
    </a>
  );
}

function ProductImage({ product, shortLabel, fallbackSrc }) {
  const src = getDisplayImageSrc(fallbackSrc) || getDisplayImageSrc(product?.image_url);
  if (!src) {
    return (
      <div className="ab-upg-photo-placeholder" data-testid={`image-placeholder-${shortLabel}`}>
        {shortLabel} image unavailable
      </div>
    );
  }
  return (
    <img
      className="ab-upg-photo"
      src={src}
      alt={product?.name || shortLabel}
      data-testid={`image-${shortLabel}`}
    />
  );
}

function RangeChartSvg({
  ownedMaxFt,
  consideredMaxFt,
  scaleMax,
  layout,
  className,
  testId,
  ownedLabel,
  consideredLabel,
  rangeGain,
}) {
  const x = (ft) => layout.left + (ft / scaleMax) * layout.track;
  const x0 = x(0);
  const xOwned = x(ownedMaxFt);
  const xConsidered = x(consideredMaxFt);
  const xMax = x(scaleMax);
  const shorterFt = Math.min(ownedMaxFt, consideredMaxFt);
  const longerFt = Math.max(ownedMaxFt, consideredMaxFt);
  const xShorter = x(shorterFt);
  const ownedMid = layout.ownedBarY + layout.barH / 2 + 4;
  const consideredMid = layout.consideredBarY + layout.barH / 2 + 4;
  const showExtra = Boolean(rangeGain && consideredMaxFt > ownedMaxFt);
  const extraClipId = `${testId}-extra-clip`;
  const extraMidX = (xOwned + xConsidered) / 2;

  return (
    <svg
      className={className}
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      data-testid={testId}
    >
      {showExtra ? (
        <defs>
          <clipPath id={extraClipId}>
            <rect
              x={x0}
              y={layout.consideredBarY}
              width={xConsidered - x0}
              height={layout.barH}
              rx="3"
            />
          </clipPath>
        </defs>
      ) : null}
      <line x1={x0} y1={layout.axisY} x2={xMax} y2={layout.axisY} stroke="#9aa8bc" strokeWidth="1.5" />
      <text x={x0} y={layout.tickY} textAnchor="start" className="ab-cmp-svg-tick">
        0 ft
      </text>
      <text x={xShorter} y={layout.tickY} textAnchor="middle" className="ab-cmp-svg-tick">
        {shorterFt} ft
      </text>
      <text x={xMax} y={layout.tickY} textAnchor="end" className="ab-cmp-svg-tick">
        {longerFt} ft
      </text>
      <text x={x0} y={layout.ownedLabelY} className="ab-cmp-svg-path">
        {ownedLabel}
      </text>
      {showExtra ? (
        <text
          x={xOwned + 6}
          y={ownedMid}
          textAnchor="start"
          className="ab-upg-range-value"
          data-testid={`${testId}-owned-end`}
        >
          {ownedMaxFt} ft
        </text>
      ) : null}
      <text x={x0} y={layout.consideredLabelY} className="ab-cmp-svg-path">
        {consideredLabel}
      </text>
      {showExtra ? (
        <text
          x={xMax}
          y={layout.consideredLabelY}
          textAnchor="end"
          className="ab-upg-range-value"
          data-testid={`${testId}-considered-end`}
        >
          {consideredMaxFt} ft
        </text>
      ) : null}
      {showExtra ? (
        <rect
          x={x0}
          y={layout.ownedBarY}
          width={xMax - x0}
          height={layout.barH}
          fill="#e6edf6"
          rx="3"
        />
      ) : null}
      <rect x={x0} y={layout.ownedBarY} width={xOwned - x0} height={layout.barH} fill="#132033" rx="3" />
      {showExtra ? (
        <rect
          x={x0}
          y={layout.consideredBarY}
          width={xMax - x0}
          height={layout.barH}
          fill="#e6edf6"
          rx="3"
        />
      ) : null}
      {showExtra ? (
        <g clipPath={`url(#${extraClipId})`}>
          <rect
            x={x0}
            y={layout.consideredBarY}
            width={xOwned - x0}
            height={layout.barH}
            fill="#0f50f2"
          />
          <rect
            x={xOwned}
            y={layout.consideredBarY}
            width={xConsidered - xOwned}
            height={layout.barH}
            fill="#6b9bff"
            data-testid={`${testId}-extra`}
            data-range-extra-start={ownedMaxFt}
            data-range-extra-end={consideredMaxFt}
          />
        </g>
      ) : (
        <rect
          x={x0}
          y={layout.consideredBarY}
          width={xConsidered - x0}
          height={layout.barH}
          fill="#0f50f2"
          rx="3"
        />
      )}
      {showExtra ? (
        <line
          className="ab-upg-range-baseline"
          x1={xOwned}
          y1={layout.ownedBarY}
          x2={xOwned}
          y2={layout.consideredBarY + layout.barH}
          data-testid={`${testId}-baseline`}
        />
      ) : null}
      {showExtra ? (
        <text
          x={extraMidX}
          y={consideredMid}
          textAnchor="middle"
          className="ab-upg-range-extra-label"
          data-testid={`${testId}-extra-label`}
        >
          {rangeGain.plusLabel}
        </text>
      ) : null}
      {showExtra ? null : (
        <>
          <text
            x={xOwned - layout.endInset}
            y={ownedMid}
            textAnchor="end"
            className="ab-cmp-svg-end-light"
            data-testid={`${testId}-owned-end`}
          >
            {ownedMaxFt} ft
          </text>
          <text
            x={xConsidered - layout.endInset}
            y={consideredMid}
            textAnchor="end"
            className="ab-cmp-svg-end-light"
            data-testid={`${testId}-considered-end`}
          >
            {consideredMaxFt} ft
          </text>
        </>
      )}
    </svg>
  );
}

function UpgradeRangeGraphic({
  ownedMaxFt,
  consideredMaxFt,
  ownedLabel,
  consideredLabel,
  a11y,
  rangeGain,
}) {
  const scaleMax = Math.max(ownedMaxFt, consideredMaxFt);
  return (
    <figure
      className={['ab-cmp-range', 'ab-upg-range', rangeGain ? 'ab-upg-range-emphasis' : '']
        .filter(Boolean)
        .join(' ')}
      data-testid="upgrade-range-graphic"
    >
      <p className="ab-cmp-sr-only" data-testid="upgrade-range-a11y">
        {a11y}
      </p>
      <RangeChartSvg
        ownedMaxFt={ownedMaxFt}
        consideredMaxFt={consideredMaxFt}
        scaleMax={scaleMax}
        layout={RANGE_LAYOUT_MOBILE}
        className="ab-cmp-range-svg ab-cmp-range-mobile"
        testId="upgrade-range-mobile"
        ownedLabel={ownedLabel}
        consideredLabel={consideredLabel}
        rangeGain={rangeGain}
      />
      <RangeChartSvg
        ownedMaxFt={ownedMaxFt}
        consideredMaxFt={consideredMaxFt}
        scaleMax={scaleMax}
        layout={RANGE_LAYOUT_DESKTOP}
        className="ab-cmp-range-svg ab-cmp-range-desktop"
        testId="upgrade-range-desktop"
        ownedLabel={ownedLabel}
        consideredLabel={consideredLabel}
        rangeGain={rangeGain}
      />
      {rangeGain ? (
        <p className="ab-upg-range-gain" data-testid="upgrade-range-gain">
          <span className="ab-cmp-sr-only">{rangeGain.plusLabel}</span>
          <span className="ab-upg-range-gain-pct">{rangeGain.percentLabel}</span>
        </p>
      ) : null}
    </figure>
  );
}

function beamAimDeg(mode) {
  if (mode === 'forward') return BEAM_FORWARD_PITCH;
  if (mode === 'down') return 90;
  return -90;
}

function beamSpan(mode, sweepDeg) {
  const aim = beamAimDeg(mode);
  return { start: aim - sweepDeg / 2, end: aim + sweepDeg / 2 };
}

function beamPolar(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function beamFanPath(cx, cy, r, startDeg, endDeg) {
  const start = beamPolar(cx, cy, r, startDeg);
  const end = beamPolar(cx, cy, r, endDeg);
  const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M ${cx.toFixed(2)} ${cy.toFixed(2)} L ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)} Z`;
}

function beamRefLabelAnchor(mode, cx, cy, refR, refSpan) {
  const edgeDeg = mode === 'down' ? refSpan.start : refSpan.end;
  const along = mode === 'forward' ? 0.7 : 0.58;
  const inward = mode === 'forward' ? 0.38 : 0.28;
  const edge = beamPolar(cx, cy, refR * along, edgeDeg);
  const aim = beamPolar(cx, cy, refR * along, beamAimDeg(mode));
  return {
    x: edge.x + (aim.x - edge.x) * inward,
    y: edge.y + (aim.y - edge.y) * inward,
    anchor: mode === 'forward' ? 'middle' : 'start',
  };
}

function BeamOrigin({ cx, cy }) {
  return (
    <g className="ab-upg-beam-bar ab-upg-beam-origin">
      <circle cx={cx} cy={cy} r="5" />
      <circle className="ab-upg-beam-origin-core" cx={cx} cy={cy} r="2" />
    </g>
  );
}

function BeamSceneSvg({ scene, sweepDeg, model, testId, referenceSweep }) {
  const { width, height, cx, cy, r, waterY, bedY, mode, view } = scene;
  const clipId = `${testId}-water`;
  const fillId = `${testId}-fan`;
  const span = beamSpan(mode, sweepDeg);
  const fan = beamFanPath(cx, cy, r, span.start, span.end);
  const start = beamPolar(cx, cy, r, span.start);
  const end = beamPolar(cx, cy, r, span.end);
  const showRef = model !== 'lvs34' && referenceSweep != null && referenceSweep < sweepDeg;
  const refSpan = showRef ? beamSpan(mode, referenceSweep) : null;
  const refR = r * 0.96;
  const refFan = refSpan ? beamFanPath(cx, cy, refR, refSpan.start, refSpan.end) : null;
  const refLabel = refSpan ? beamRefLabelAnchor(mode, cx, cy, refR, refSpan) : null;
  const speckles = BEAM_SPECKLES[mode];
  return (
    <svg
      className="ab-upg-beam-svg ab-upg-beam-scene"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      data-testid={testId}
      data-beam-mode={mode}
      data-beam-model={model}
      data-beam-sweep={sweepDeg}
      data-beam-ref-sweep={showRef ? referenceSweep : undefined}
      data-beam-radius={r}
      data-beam-view={view}
      data-beam-aim={mode === 'forward' ? 'forward-down' : mode}
      data-origin-x={cx}
      data-origin-y={cy}
      data-water-y={waterY}
      data-beam-pitch={mode === 'forward' ? BEAM_FORWARD_PITCH : undefined}
    >
      <defs>
        <radialGradient id={fillId} cx={cx} cy={cy} r={r} gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#3d72f5" stopOpacity="0.58" />
          <stop offset="72%" stopColor="#0f50f2" stopOpacity="0.34" />
          <stop offset="100%" stopColor="#0f50f2" stopOpacity="0.1" />
        </radialGradient>
        {waterY != null ? (
          <clipPath id={clipId} className="ab-upg-beam-clip">
            <rect x="0" y={waterY} width={width} height={height - waterY} />
          </clipPath>
        ) : (
          <clipPath id={clipId} className="ab-upg-beam-clip">
            <rect x="0" y="0" width={width} height={height} />
          </clipPath>
        )}
      </defs>
      {mode === 'perspective' ? (
        <>
          <rect className="ab-upg-beam-field" x="0" y="0" width={width} height={height} />
          <path
            className="ab-upg-beam-contour"
            d="M 18 118 C 86 102 148 126 214 108 C 268 94 318 116 344 104"
          />
          <path
            className="ab-upg-beam-weed"
            data-beam-env="weed"
            d="M 28 72 Q 88 58 140 70 Q 196 50 248 66 Q 300 54 338 68"
          />
          <path className="ab-upg-beam-brush" d="M 52 96 C 70 86 84 92 96 108 C 78 114 60 110 52 96 Z" />
        </>
      ) : (
        <>
          <rect className="ab-upg-beam-air" x="0" y="0" width={width} height={waterY} />
          <rect className="ab-upg-beam-water" x="0" y={waterY} width={width} height={height - waterY} />
          <path
            className="ab-upg-beam-bed"
            data-beam-env="bottom"
            d={`M 10 ${bedY} C ${width * 0.32} ${bedY - 10} ${width * 0.62} ${bedY - 6} ${width - 10} ${bedY + 2} L ${width - 10} ${height} L 10 ${height} Z`}
          />
          <text className="ab-upg-beam-env-label" x="16" y={waterY - 6}>
            SURFACE
          </text>
          <text className="ab-upg-beam-env-label" x="16" y={bedY - 8}>
            BOTTOM
          </text>
        </>
      )}
      <g clipPath={`url(#${clipId})`}>
        <path className="ab-upg-beam-fan" d={fan} fill={`url(#${fillId})`} />
        <line className="ab-upg-beam-ray" x1={cx} y1={cy} x2={start.x} y2={start.y} />
        <line className="ab-upg-beam-ray" x1={cx} y1={cy} x2={end.x} y2={end.y} />
        {refFan ? (
          <path className="ab-upg-beam-ref" data-beam-ref="lvs34" d={refFan} />
        ) : null}
        {speckles.map(([x, y], index) => (
          <circle key={index} className="ab-upg-beam-speckle" cx={x} cy={y} r="1.15" />
        ))}
      </g>
      {waterY != null ? (
        <line className="ab-upg-beam-waterline" x1="8" y1={waterY} x2={width - 8} y2={waterY} />
      ) : null}
      {refLabel ? (
        <text
          className="ab-upg-beam-ref-label"
          data-beam-ref-label={`LVS34 ${referenceSweep}°`}
          x={refLabel.x.toFixed(2)}
          y={refLabel.y.toFixed(2)}
          textAnchor={refLabel.anchor}
        >
          LVS34 {referenceSweep}°
        </text>
      ) : null}
      <BeamOrigin cx={cx} cy={cy} />
    </svg>
  );
}

function BeamModelCard({ scene, sweepDeg, model, label, testId, referenceSweep }) {
  return (
    <div className={`ab-upg-beam-card ab-upg-beam-card-${model}`} data-testid={testId}>
      <p className="ab-upg-beam-modelhead">
        <span className="ab-upg-beam-modelname">{label}</span>
        <span className="ab-upg-beam-modeldeg">{sweepDeg}°</span>
      </p>
      {referenceSweep != null && model !== 'lvs34' ? (
        <p className="ab-cmp-sr-only">
          Dashed line shows the LVS34 field of view inside the wider {label} beam.
        </p>
      ) : null}
      <BeamSceneSvg
        scene={scene}
        sweepDeg={sweepDeg}
        model={model}
        testId={`${testId}-scene`}
        referenceSweep={referenceSweep}
      />
    </div>
  );
}

function UpgradeBeamGraphic({
  label,
  ownedSweep,
  consideredSweep,
  interpretation,
  mode,
  testId,
  consideredLabel,
  consideredModel,
}) {
  const scene = BEAM_SCENES[mode];
  return (
    <figure className={`ab-upg-beam ab-upg-beam-${mode}`} data-testid={testId}>
      <p className="ab-upg-beam-label">{label}</p>
      <p className="ab-upg-beam-orient" data-testid={`${testId}-orient`}>
        {scene.orientation}
      </p>
      <p className="ab-cmp-sr-only">
        {label} field of view. {scene.orientation}. LVS34 is {ownedSweep} degrees. {consideredLabel} is{' '}
        {consideredSweep} degrees. {interpretation}
      </p>
      <div className="ab-upg-beam-pair">
        <BeamModelCard
          scene={scene}
          sweepDeg={ownedSweep}
          model="lvs34"
          label="LVS34"
          testId={`${testId}-lvs34`}
        />
        <BeamModelCard
          scene={scene}
          sweepDeg={consideredSweep}
          model={consideredModel}
          label={consideredLabel}
          testId={`${testId}-${consideredModel}`}
          referenceSweep={ownedSweep}
        />
      </div>
      <p className="ab-upg-beam-note">{interpretation}</p>
    </figure>
  );
}

function bloomPath(rx, ry) {
  return [
    `M ${(-0.9 * rx).toFixed(2)} ${(0.08 * ry).toFixed(2)}`,
    `C ${(-1.16 * rx).toFixed(2)} ${(-0.52 * ry).toFixed(2)} ${(-0.44 * rx).toFixed(2)} ${(-1.2 * ry).toFixed(2)} ${(0.1 * rx).toFixed(2)} ${(-0.64 * ry).toFixed(2)}`,
    `C ${(0.5 * rx).toFixed(2)} ${(-1.24 * ry).toFixed(2)} ${(1.18 * rx).toFixed(2)} ${(-0.46 * ry).toFixed(2)} ${(0.86 * rx).toFixed(2)} ${(0.2 * ry).toFixed(2)}`,
    `C ${(1.24 * rx).toFixed(2)} ${(0.7 * ry).toFixed(2)} ${(0.32 * rx).toFixed(2)} ${(1.18 * ry).toFixed(2)} ${(-0.18 * rx).toFixed(2)} ${(0.6 * ry).toFixed(2)}`,
    `C ${(-0.56 * rx).toFixed(2)} ${(1.1 * ry).toFixed(2)} ${(-1.2 * rx).toFixed(2)} ${(0.46 * ry).toFixed(2)} ${(-0.9 * rx).toFixed(2)} ${(0.08 * ry).toFixed(2)}`,
    'Z',
  ].join(' ');
}

function mergedEnvelopeRadius(spacingPx, resolvedRadius, laneW) {
  const half = spacingPx / 2;
  const justOverlap = half + Math.max(6, resolvedRadius * 0.1);
  const broaderThanHd = resolvedRadius + 12;
  return Math.min(laneW / 2 - half - 12, Math.max(justOverlap, broaderThanHd));
}

function returnRadius(spacingPx, thresholdIn, pxPerIn, state) {
  const raw = (thresholdIn * pxPerIn) / 2;
  const half = spacingPx / 2;
  if (state === 'merged') {
    return Math.max(raw, half + 8);
  }
  return Math.min(raw, half - 8);
}

function SonarSpeckles({ cx, cy, rx, ry, fillId }) {
  return SEP_SPECKLES.map(([nx, ny, r], index) => (
    <circle
      key={`${nx}-${ny}-${index}`}
      className="ab-upg-sep-speckle"
      cx={cx + nx * rx}
      cy={cy + ny * ry}
      r={r}
      fill={`url(#${fillId})`}
    />
  ));
}

function TargetPoint({ cx, cy }) {
  return <circle className="ab-upg-sep-target" cx={cx} cy={cy} r="2.15" />;
}

function SplitReturn({ cx, cy, rx, ry, ids, flip }) {
  const sx = flip ? -1 : 1;
  return (
    <g className="ab-upg-sep-return ab-upg-sep-return-split" transform={`translate(${cx} ${cy}) scale(${sx} 1)`}>
      <path
        className="ab-upg-sep-bloom"
        d={bloomPath(rx * 1.08, ry * 1.1)}
        fill={`url(#${ids.softFill})`}
        filter={`url(#${ids.soft})`}
        opacity="0.55"
      />
      <path
        className="ab-upg-sep-bloom"
        d={bloomPath(rx, ry)}
        fill={`url(#${ids.hotFill})`}
        filter={`url(#${ids.sonar})`}
      />
      <path className="ab-upg-sep-core" d={bloomPath(rx * 0.34, ry * 0.32)} fill={`url(#${ids.coreFill})`} />
      <SonarSpeckles cx={0} cy={0} rx={rx} ry={ry} fillId={ids.hotFill} />
    </g>
  );
}

function MergedTargetReturn({ cx, cy, rx, ry, ids, flip, side }) {
  const sx = flip ? -1 : 1;
  return (
    <g
      className="ab-upg-sep-merged-envelope"
      data-sep-merged-envelope={side}
      data-sep-envelope-cx={cx.toFixed(1)}
      data-sep-envelope-rx={rx.toFixed(1)}
      transform={`translate(${cx} ${cy}) scale(${sx} 1)`}
    >
      <path
        className="ab-upg-sep-bloom"
        d={bloomPath(rx * 1.12, ry * 1.14)}
        fill={`url(#${ids.softFill})`}
        filter={`url(#${ids.soft})`}
        opacity="0.62"
      />
      <path
        className="ab-upg-sep-bloom"
        d={bloomPath(rx, ry)}
        fill={`url(#${ids.hotFill})`}
        filter={`url(#${ids.sonar})`}
      />
      <path className="ab-upg-sep-core" d={bloomPath(Math.min(rx * 0.28, 17), ry * 0.32)} fill={`url(#${ids.coreFill})`} />
      <SonarSpeckles cx={0} cy={0} rx={rx} ry={ry} fillId={ids.hotFill} />
    </g>
  );
}

function MergedReturn({ leftCx, rightCx, cy, rx, ry, ids }) {
  return (
    <g
      className="ab-upg-sep-return ab-upg-sep-return-merged"
      data-sep-merged-shape="overlap"
      data-sep-merged-envelopes="2"
      data-sep-merged-ry={ry}
    >
      <MergedTargetReturn cx={leftCx} cy={cy} rx={rx} ry={ry} ids={ids} side="left" />
      <MergedTargetReturn cx={rightCx} cy={cy} rx={rx} ry={ry} ids={ids} flip side="right" />
    </g>
  );
}

function SepBracket({ x1, x2, y, label }) {
  return (
    <g className="ab-upg-sep-bracket">
      <line className="ab-upg-sep-measure" x1={x1} y1={y} x2={x2} y2={y} />
      <line className="ab-upg-sep-measure" x1={x1} y1={y - 3.2} x2={x1} y2={y + 3.2} />
      <line className="ab-upg-sep-measure" x1={x2} y1={y - 3.2} x2={x2} y2={y + 3.2} />
      <text x={(x1 + x2) / 2} y={y - 6} textAnchor="middle" className="ab-upg-sep-inch">
        {label}
      </text>
    </g>
  );
}

function SepFieldRow({
  label,
  spacingIn,
  thresholdIn,
  consideredThresholdIn,
  state,
  layout,
  cy,
  ids,
  rowKey,
}) {
  const gap = spacingIn * layout.pxPerIn;
  const leftCx = layout.cx - gap / 2;
  const rightCx = layout.cx + gap / 2;
  const laneY = cy - layout.laneH / 2 + 6;
  const bloomY = cy + 8;
  const radius = returnRadius(gap, thresholdIn, layout.pxPerIn, state);
  const resolvedRadius = returnRadius(gap, consideredThresholdIn, layout.pxPerIn, 'split');
  const envelopeRx = mergedEnvelopeRadius(gap, resolvedRadius, layout.laneW);
  const ry = Math.min(state === 'merged' ? 16 : 15, radius * (state === 'merged' ? 0.28 : 0.52));
  const measureY = bloomY + ry + 10;
  return (
    <g
      data-testid={`upgrade-sep-row-${rowKey}`}
      data-sep-spacing={spacingIn}
      data-sep-state={state}
      data-sep-gap={gap}
      data-sep-merged-half={state === 'merged' ? (gap / 2 + envelopeRx).toFixed(1) : undefined}
      data-sep-merged-rx={state === 'merged' ? envelopeRx.toFixed(1) : undefined}
      data-sep-resolved-radius={state === 'split' ? radius.toFixed(1) : undefined}
    >
      <rect
        className="ab-upg-sep-lane"
        x={layout.laneX}
        y={laneY}
        width={layout.laneW}
        height={layout.laneH}
        rx="8"
        fill={`url(#${ids.field})`}
      />
      <text x={layout.laneX + 12} y={laneY + 15} className="ab-upg-sep-row-label">
        {label}
      </text>
      <text
        x={layout.laneX + layout.laneW - 12}
        y={laneY + 15}
        textAnchor="end"
        className="ab-upg-sep-state"
      >
        {state === 'merged' ? 'MERGED' : 'RESOLVED'}
      </text>
      {state === 'merged' ? (
        <MergedReturn
          leftCx={leftCx}
          rightCx={rightCx}
          cy={bloomY}
          rx={envelopeRx}
          ry={ry}
          ids={ids}
        />
      ) : (
        <>
          <SplitReturn cx={leftCx} cy={bloomY} rx={radius} ry={ry} ids={ids} />
          <SplitReturn cx={rightCx} cy={bloomY} rx={radius} ry={ry} ids={ids} flip />
        </>
      )}
      <TargetPoint cx={leftCx} cy={bloomY} />
      <TargetPoint cx={rightCx} cy={bloomY} />
      <SepBracket x1={leftCx} x2={rightCx} y={measureY} label={`${spacingIn.toFixed(1)}" apart`} />
    </g>
  );
}

function SepFieldSvg({
  layout,
  className,
  testId,
  spacingIn,
  ownedThresholdIn,
  consideredThresholdIn,
  ownedKey,
  consideredKey,
  consideredLabel,
}) {
  const ids = {
    hotFill: `${testId}-hot`,
    softFill: `${testId}-softfill`,
    coreFill: `${testId}-core`,
    field: `${testId}-field`,
    sonar: `${testId}-sonar`,
    soft: `${testId}-soft`,
  };
  const rows = [
    {
      label: 'LVS34',
      thresholdIn: ownedThresholdIn,
      state: 'merged',
      cy: layout.ownedY,
      rowKey: ownedKey,
    },
    {
      label: consideredLabel,
      thresholdIn: consideredThresholdIn,
      state: 'split',
      cy: layout.consideredY,
      rowKey: consideredKey,
    },
  ];
  return (
    <svg
      className={className}
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      data-testid={testId}
    >
      <defs>
        <linearGradient id={ids.field} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#101924" />
          <stop offset="100%" stopColor="#0a1018" />
        </linearGradient>
        <radialGradient id={ids.hotFill} cx="36%" cy="34%" r="68%">
          <stop offset="0%" stopColor="#fff4b0" />
          <stop offset="28%" stopColor="#ffcc4d" />
          <stop offset="62%" stopColor="#ff8a1a" />
          <stop offset="100%" stopColor="#c44a00" stopOpacity="0.18" />
        </radialGradient>
        <radialGradient id={ids.softFill} cx="40%" cy="40%" r="75%">
          <stop offset="0%" stopColor="#ffb000" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#c44a00" stopOpacity="0.05" />
        </radialGradient>
        <radialGradient id={ids.coreFill} cx="40%" cy="38%" r="60%">
          <stop offset="0%" stopColor="#fff8d2" />
          <stop offset="55%" stopColor="#ffe14a" />
          <stop offset="100%" stopColor="#ff9a1a" stopOpacity="0.7" />
        </radialGradient>
        <filter id={ids.sonar} x="-35%" y="-45%" width="170%" height="190%">
          <feTurbulence type="fractalNoise" baseFrequency="0.085" numOctaves="2" seed="4" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="3.4" xChannelSelector="R" yChannelSelector="G" />
          <feGaussianBlur stdDeviation="0.55" />
        </filter>
        <filter id={ids.soft} x="-40%" y="-50%" width="180%" height="200%">
          <feGaussianBlur stdDeviation="2.1" />
        </filter>
      </defs>
      {rows.map((row) => (
        <SepFieldRow
          key={row.rowKey}
          label={row.label}
          spacingIn={spacingIn}
          thresholdIn={row.thresholdIn}
          consideredThresholdIn={consideredThresholdIn}
          state={row.state}
          layout={layout}
          cy={row.cy}
          ids={ids}
          rowKey={row.rowKey}
        />
      ))}
    </svg>
  );
}

function SepReadout({
  ownedValue,
  consideredValue,
  ownedNote,
  takeaway,
  support,
  takeawayTestId,
  ownedTestId,
  consideredTestId,
  thresholdTestId,
  consideredLabel,
}) {
  return (
    <div className="ab-upg-sep-readout">
      <p className="ab-upg-sep-threshold-kicker">Minimum spacing to show two targets</p>
      <dl className="ab-upg-sep-thresholds" data-testid={thresholdTestId}>
        <div className="ab-upg-sep-threshold">
          <dt>LVS34</dt>
          <dd>
            <span className="ab-upg-sep-num" data-testid={ownedTestId}>
              {ownedValue}
            </span>
            {ownedNote ? <span className="ab-upg-sep-est-text">{ownedNote}</span> : null}
          </dd>
        </div>
        <div className="ab-upg-sep-threshold">
          <dt>{consideredLabel}</dt>
          <dd>
            <span className="ab-upg-sep-num" data-testid={consideredTestId}>
              {consideredValue}
            </span>
          </dd>
        </div>
      </dl>
      <p className="ab-upg-sep-takeaway" data-testid={takeawayTestId}>
        {takeaway}
      </p>
      {support ? <p className="ab-upg-sep-support">{support}</p> : null}
    </div>
  );
}

function UpgradeSeparationGraphic({ editorial }) {
  const sep30 = editorial.sep30;
  const sep100 = editorial.sep100;
  const consideredLabel = editorial.sepConsideredLabel;
  const mobile30 = sepFieldLayout('mobile', sep30.exampleIn, sep30.ownedThresholdIn);
  const desktop30 = sepFieldLayout('desktop', sep30.exampleIn, sep30.ownedThresholdIn);
  const mobile100 = sepFieldLayout('mobile', sep100.exampleIn, sep100.ownedThresholdIn);
  const desktop100 = sepFieldLayout('desktop', sep100.exampleIn, sep100.ownedThresholdIn);
  return (
    <figure className="ab-upg-sep-100" data-testid="upgrade-separation">
      <p className="ab-cmp-sr-only" data-testid="upgrade-sep-a11y">
        {editorial.sepA11y}
      </p>
      <p className="ab-upg-sep-legend" data-testid="upgrade-sep-legend">
        <span className="ab-upg-sep-legend-item">
          <span className="ab-upg-sep-legend-dot" aria-hidden="true" />
          Actual target
        </span>
        <span className="ab-upg-sep-legend-item">
          <span className="ab-upg-sep-legend-glow" aria-hidden="true" />
          Sonar return
        </span>
      </p>
      <div className="ab-upg-sep-block" data-testid="upgrade-sep-30">
        <h3 className="ab-upg-sep-100-title">Closer look at 30 ft</h3>
        <div className="ab-upg-sep-100-split">
          <div className="ab-upg-sep-visual">
            <SepFieldSvg
              layout={mobile30}
              spacingIn={sep30.exampleIn}
              ownedThresholdIn={sep30.ownedThresholdIn}
              consideredThresholdIn={sep30.consideredThresholdIn}
              ownedKey="30-owned"
              consideredKey="30-considered"
              consideredLabel={consideredLabel}
              className="ab-upg-sep-svg ab-upg-sep-mobile"
              testId="upgrade-sep-30-mobile"
            />
            <SepFieldSvg
              layout={desktop30}
              spacingIn={sep30.exampleIn}
              ownedThresholdIn={sep30.ownedThresholdIn}
              consideredThresholdIn={sep30.consideredThresholdIn}
              ownedKey="30-owned"
              consideredKey="30-considered"
              consideredLabel={consideredLabel}
              className="ab-upg-sep-svg ab-upg-sep-desktop"
              testId="upgrade-sep-30-desktop"
            />
          </div>
          <SepReadout
            thresholdTestId="upgrade-sep-30-thresholds"
            ownedValue={`~${sep30.ownedThresholdIn.toFixed(1)}"`}
            consideredValue={`${sep30.consideredThresholdIn.toFixed(1)}"`}
            ownedNote="ANGLER BUILT ESTIMATE"
            ownedTestId="upgrade-sep-30-owned"
            consideredTestId="upgrade-sep-30-hd"
            consideredLabel={consideredLabel}
            takeawayTestId="upgrade-sep-30-takeaway"
            takeaway={sep30.takeaway}
            support={sep30.support}
          />
        </div>
      </div>
      <div className="ab-upg-sep-block" data-testid="upgrade-sep-100-block">
        <h3 className="ab-upg-sep-100-title">Closer look at 100 ft</h3>
        <div className="ab-upg-sep-100-split">
          <div className="ab-upg-sep-visual">
            <SepFieldSvg
              layout={mobile100}
              spacingIn={sep100.exampleIn}
              ownedThresholdIn={sep100.ownedThresholdIn}
              consideredThresholdIn={sep100.consideredThresholdIn}
              ownedKey="owned"
              consideredKey="considered"
              consideredLabel={consideredLabel}
              className="ab-upg-sep-svg ab-upg-sep-mobile"
              testId="upgrade-sep-mobile"
            />
            <SepFieldSvg
              layout={desktop100}
              spacingIn={sep100.exampleIn}
              ownedThresholdIn={sep100.ownedThresholdIn}
              consideredThresholdIn={sep100.consideredThresholdIn}
              ownedKey="owned"
              consideredKey="considered"
              consideredLabel={consideredLabel}
              className="ab-upg-sep-svg ab-upg-sep-desktop"
              testId="upgrade-sep-desktop"
            />
          </div>
          <SepReadout
            thresholdTestId="upgrade-sep-100-thresholds"
            ownedValue={`${sep100.ownedThresholdIn.toFixed(1)}"`}
            consideredValue={`${sep100.consideredThresholdIn.toFixed(1)}"`}
            ownedTestId="upgrade-sep-100-owned"
            consideredTestId="upgrade-sep-100-hd"
            consideredLabel={consideredLabel}
            takeawayTestId="upgrade-sep-100"
            takeaway={sep100.takeaway}
            support={sep100.support}
          />
        </div>
      </div>
      <p className="ab-upg-sep-example-note" data-testid="upgrade-sep-example-note">
        Illustrative sonar returns based on the separation values shown.
      </p>
    </figure>
  );
}

function useRetailerOptionImpressions({ moduleRef, options, context }) {
  const seenRef = useRef(new Set());
  const sectionSeenRef = useRef(false);

  useEffect(() => {
    const node = moduleRef.current;
    if (!node || !options.length) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const fireVisible = () => {
      if (!sectionSeenRef.current) {
        sectionSeenRef.current = true;
        trackPriceSectionView({
        pageSource: AFFILIATE_PAGE_SOURCE.UPGRADE_COMPARISON,
        productSlug: context.productSlug,
        product: context.product,
        comparisonId: context.comparisonId,
        freshnessState: context.hasCurrent ? 'CURRENT' : 'NONE',
        retailerCount: options.length,
        hasCurrentVerifiedPrice: Boolean(context.hasCurrent),
        placement: 'current_prices',
      });
      }
      options.forEach((row) => {
        const key = `${row.retailer}|${row.href}|${row.position}`;
        if (seenRef.current.has(key)) return;
        seenRef.current.add(key);
        trackRetailerOptionImpression({
          pageSource: AFFILIATE_PAGE_SOURCE.UPGRADE_COMPARISON,
          comparisonId: context.comparisonId,
          productSlug: context.productSlug,
          product: context.product,
          retailer: row.retailer,
          position: row.position,
          price: row.price,
          freshnessState: row.freshnessState,
          isLowestPrice: row.isLowestPrice,
          isTiedLowest: row.isTiedLowest,
          placement: 'current_prices',
        });
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        fireVisible();
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    moduleRef,
    options,
    context.comparisonId,
    context.productSlug,
    context.product,
    context.hasCurrent,
  ]);
}

function useMobileStickyViewport() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(STICKY_MOBILE_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(STICKY_MOBILE_MEDIA_QUERY);
    const onChange = () => setIsMobile(media.matches);
    onChange();
    if (media.addEventListener) media.addEventListener('change', onChange);
    else media.addListener(onChange);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', onChange);
      else media.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

function useStickyPriceBarVisibility({ triggerRef, pricesRef, enabled }) {
  const [passedTrigger, setPassedTrigger] = useState(false);
  const [atPrices, setAtPrices] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setPassedTrigger(false);
      setAtPrices(false);
      return undefined;
    }
    const trigger = triggerRef.current;
    const prices = pricesRef.current;
    if (!trigger || !prices || typeof IntersectionObserver === 'undefined') return undefined;

    const triggerObserver = new IntersectionObserver(
      (entries) => {
        setPassedTrigger(hasReachedComparisonStart(entries[0]));
      },
      { threshold: 0 }
    );
    const pricesObserver = new IntersectionObserver(
      (entries) => {
        setAtPrices(hasReachedRetailerModule(entries[0], window.innerHeight || 0));
      },
      { threshold: 0, rootMargin: STICKY_PRICES_ROOT_MARGIN }
    );
    triggerObserver.observe(trigger);
    pricesObserver.observe(prices);

    let frame = 0;
    const syncFromLayout = () => {
      const viewportHeight = window.innerHeight || 0;
      setPassedTrigger(hasReachedComparisonStart(trigger.getBoundingClientRect().top));
      setAtPrices(hasReachedRetailerModule(prices.getBoundingClientRect().top, viewportHeight));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        syncFromLayout();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      triggerObserver.disconnect();
      pricesObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [triggerRef, pricesRef, enabled]);

  return passedTrigger && !atPrices;
}

function UpgradeStickyPriceBar({
  module,
  productLabel,
  comparisonId,
  triggerRef,
  pricesRef,
}) {
  const isMobile = useMobileStickyViewport();
  const summary = getStickyPriceBarSummary(module, { productLabel });
  const inJourney = useStickyPriceBarVisibility({
    triggerRef,
    pricesRef,
    enabled: isMobile && summary.shouldShow,
  });
  const visible = isMobile && summary.shouldShow && inJourney;
  const impressedRef = useRef(false);

  useEffect(() => {
    if (!visible || impressedRef.current) return;
    impressedRef.current = true;
    trackComparisonStickyPriceImpression({
      pageSource: AFFILIATE_PAGE_SOURCE.UPGRADE_COMPARISON,
      comparisonId,
      product: productLabel,
      price: summary.price,
      currentLowestRetailerCount: summary.currentLowestRetailerCount,
      currentRetailerCount: summary.currentRetailerCount,
      hasCurrentVerifiedPrice: summary.hasCurrent,
      placement: 'sticky_price_bar',
    });
  }, [
    visible,
    comparisonId,
    productLabel,
    summary.price,
    summary.currentLowestRetailerCount,
    summary.currentRetailerCount,
    summary.hasCurrent,
  ]);

  if (!visible) return null;

  return (
    <div
      className="ab-upg-sticky-price"
      data-testid="upgrade-sticky-price-bar"
      role="region"
      aria-label={`${productLabel} current prices`}
    >
      <div className="ab-upg-sticky-price-inner">
        <div className="ab-upg-sticky-price-copy">
          <p className="ab-upg-sticky-price-model">{productLabel}</p>
          <p className="ab-upg-sticky-price-value" data-testid="upgrade-sticky-price-value">
            {summary.hasCurrent ? summary.priceLabel : summary.fallbackLabel}
          </p>
        </div>
        <a
          className="ab-upg-sticky-price-action"
          href={summary.href}
          aria-label={summary.ariaLabel}
          data-testid="upgrade-sticky-price-action"
          onClick={() => {
            trackComparisonStickyPriceClick({
              pageSource: AFFILIATE_PAGE_SOURCE.UPGRADE_COMPARISON,
              comparisonId,
              product: productLabel,
              price: summary.price,
              currentLowestRetailerCount: summary.currentLowestRetailerCount,
              currentRetailerCount: summary.currentRetailerCount,
              hasCurrentVerifiedPrice: summary.hasCurrent,
              placement: 'sticky_price_bar',
            });
          }}
        >
          {summary.actionLabel}
        </a>
      </div>
    </div>
  );
}

function CompactCurrentOffer({ productConfig, side, comparisonId }) {
  const loading = side.loadingProduct || side.loadingOffers;
  const sideKey = productConfig.key;
  const moduleRef = useRef(null);
  const module = !loading && !side.offersError ? getRetailerPriceModule(side.offers) : null;

  useRetailerOptionImpressions({
    moduleRef,
    options: module?.visibleRetailers || [],
    context: {
      comparisonId,
      productSlug: productConfig.slug,
      product: productConfig.shortLabel,
      hasCurrent: Boolean(module?.hasCurrent),
    },
  });

  if (loading) {
    return (
      <div className="ab-upg-rpm" data-testid={`compact-offer-${sideKey}`}>
        <p className="ab-cmp-muted" data-testid={`offers-loading-${sideKey}`}>
          Loading current retailer offers…
        </p>
      </div>
    );
  }

  if (side.offersError || !side.offers.length || !module) {
    return (
      <div className="ab-upg-rpm" data-testid={`compact-offer-${sideKey}`}>
        <p className="ab-cmp-offer-kicker" data-testid={`offers-empty-${sideKey}`}>
          {productConfig.emptyOffersMessage || 'No current verified price'}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={moduleRef}
      className="ab-upg-rpm"
      data-testid="upgrade-rpm"
      data-offer-state={module.hasCurrent ? 'CURRENT' : 'NONE'}
      data-tied={module.isTiedLowest ? 'true' : 'false'}
    >
      <p className="ab-upg-rpm-kicker" data-testid="upgrade-rpm-kicker">
        {module.primaryKicker}
      </p>
      {module.hasCurrent ? (
        <p className="ab-upg-rpm-price" data-testid={`upgrade-price-${sideKey}`}>
          {module.lowestPriceLabel}
        </p>
      ) : null}
      {module.hasCurrent ? (
        <p className="ab-upg-rpm-count" data-testid="upgrade-rpm-count">
          {module.availabilityLine}
        </p>
      ) : null}
      {module.primaryRetailers.length ? (
        <div
          className={
            module.isTiedLowest
              ? 'ab-upg-rpm-primary ab-upg-rpm-primary-tied'
              : 'ab-upg-rpm-primary'
          }
          data-testid="upgrade-rpm-primary"
          data-tied-count={module.primaryCount}
        >
          {module.primaryRetailers.map((row) => (
            <RetailerChoice
              key={`${row.retailer}-${row.href}`}
              row={row}
              productSlug={productConfig.slug}
              comparisonId={comparisonId}
              product={productConfig.shortLabel}
              variant="primary"
            />
          ))}
        </div>
      ) : null}
      {module.sharedPrimaryVerifiedLine ? (
        <p className="ab-upg-rpm-shared-verified" data-testid="upgrade-rpm-shared-verified">
          {module.sharedPrimaryVerifiedLine}
        </p>
      ) : null}
      {module.otherCurrentRetailers.length ? (
        <div className="ab-upg-rpm-other" data-testid="upgrade-rpm-other">
          <p className="ab-upg-rpm-subhead">{module.otherCurrentHeading}</p>
          {module.otherCurrentRetailers.map((row) => (
            <RetailerChoice
              key={`${row.retailer}-${row.href}`}
              row={row}
              productSlug={productConfig.slug}
              comparisonId={comparisonId}
              product={productConfig.shortLabel}
              variant="other"
            />
          ))}
        </div>
      ) : null}
      <AmazonPriceNotice offers={side.offers} />
      {module.staleRetailers.length ? (
        <div className="ab-upg-rpm-stale" data-testid="upgrade-rpm-stale">
          <p className="ab-upg-rpm-subhead">{module.staleHeading}</p>
          {module.staleRetailers.map((row) => (
            <RetailerChoice
              key={`${row.retailer}-${row.href}`}
              row={row}
              productSlug={productConfig.slug}
              comparisonId={comparisonId}
              product={productConfig.shortLabel}
              variant="stale"
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

async function loadConsideredOffers(slug, setSide, cancelled) {
  try {
    const product = await getProductBySlug(slug);
    if (cancelled()) return;
    if (!product) {
      setSide({
        loadingProduct: false,
        loadingOffers: false,
        product: null,
        offers: [],
        offersError: 'not_found',
      });
      return;
    }
    setSide((prev) => ({ ...prev, loadingProduct: false, loadingOffers: true, product }));
    const result = await getOffersByProductId(product.id);
    if (cancelled()) return;
    setSide({
      loadingProduct: false,
      loadingOffers: false,
      product,
      offers: filterValidComparisonOffers(result?.data),
      offersError: null,
    });
  } catch (error) {
    if (cancelled()) return;
    setSide({
      loadingProduct: false,
      loadingOffers: false,
      product: null,
      offers: [],
      offersError: error?.message || 'offers_failed',
    });
  }
}

function UpgradeFaqPanels({
  faqs,
  config,
  facts,
  specRows,
  sources,
  conditionNote,
  consideredLabel,
}) {
  return faqs.map((faq) => (
    <details
      key={faq.testId || faq.summary}
      className="ab-cmp-disclosure-panel"
      data-testid={faq.testId}
    >
      <summary>{faq.summary}</summary>
      <div className="ab-cmp-disclosure-body">
        {faq.paragraphs?.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        {faq.inlineLink ? (
          <p>
            {faq.inlineLink.before}
            <Link to={faq.inlineLink.to}>{faq.inlineLink.label}</Link>
            {faq.inlineLink.after}
          </p>
        ) : null}
        {faq.garminClaim && config.garminCloserRangeClaim ? (
          <p data-testid="upgrade-garmin-claim">
            {config.garminClaimLabel || 'Garmin claim'}: {config.garminCloserRangeClaim}
          </p>
        ) : null}
        {faq.link ? (
          <p>
            <Link to={faq.link.to}>{faq.link.label}</Link>
          </p>
        ) : null}
        {faq.specTable ? (
          <div className="ab-cmp-table">
            <div className="ab-cmp-spec-head">
              <span />
              <span>LVS34</span>
              <span>{consideredLabel}</span>
            </div>
            {specRows.map((row) => (
              <div className="ab-cmp-row" key={row.id} data-testid={`upgrade-spec-${row.id}`}>
                <p className="ab-cmp-row-name">{row.label}</p>
                <div className="ab-cmp-row-pair">
                  <div className="ab-cmp-row-value">
                    <span className="ab-cmp-side-label">LVS34</span>
                    <span className="ab-cmp-row-num">{row.a}</span>
                  </div>
                  <div className="ab-cmp-row-value">
                    <span className="ab-cmp-side-label">{consideredLabel}</span>
                    <span className="ab-cmp-row-num">{row.b}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {faq.sources ? (
          <>
            <ul className="ab-cmp-sources">
              {sources.map((source) => (
                <li key={source.id}>
                  <a href={source.href} target="_blank" rel="noopener noreferrer">
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
            {conditionNote ? <p className="ab-cmp-source-note">{conditionNote}</p> : null}
          </>
        ) : null}
      </div>
    </details>
  ));
}

export default function UpgradeComparisonPage({ config }) {
  const owned = config.ownedProduct;
  const considered = config.consideredProduct;
  const facts = getUpgradeComparisonFacts(owned.slug, considered.slug);
  const editorial = getUpgradeEditorial(config, facts);
  const specRows = buildUpgradeSpecRows(owned.slug, considered.slug);
  const sources = getUpgradeComparisonSourceLinks({
    includeLvs44: config.id === 'lvs34-vs-lvs44',
  });
  const conditionNote = getManufacturerConditionNote();
  const [consideredSide, setConsideredSide] = useState({
    loadingProduct: true,
    loadingOffers: false,
    product: null,
    offers: [],
    offersError: null,
  });
  const comparisonStartRef = useRef(null);
  const pricesRef = useRef(null);
  const stickyModule =
    !consideredSide.loadingProduct &&
    !consideredSide.loadingOffers &&
    !consideredSide.offersError
      ? getRetailerPriceModule(consideredSide.offers)
      : null;

  usePageSeo({
    title: config.documentTitle,
    description: config.metaDescription,
    path: config.path,
  });

  useEffect(() => {
    let cancelled = false;
    loadConsideredOffers(considered.offerSlug || considered.slug, setConsideredSide, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [considered.offerSlug, considered.slug]);

  const showBeam =
    facts.ownedForwardSweep != null &&
    facts.consideredForwardSweep != null &&
    facts.ownedDownSweep != null &&
    facts.consideredDownSweep != null;
  const showRange = facts.ownedMaxFt != null && facts.consideredMaxFt != null;
  const showWeight =
    facts.ownedWeightLabel &&
    facts.glsWeightLabel &&
    facts.publishedComponentLabel &&
    facts.consideredWeightLabel;
  const visibleOrder = editorial.sectionOrder.filter((id) => {
    if (id === 'beam') return showBeam;
    if (id === 'range') return showRange;
    return true;
  });
  const triggerSectionId = visibleOrder[0];
  const isTrigger = (id) => id === triggerSectionId;

  const beamSection = showBeam ? (
    <section
      ref={isTrigger('beam') ? comparisonStartRef : undefined}
      className="ab-upg-section ab-upg-section-light"
      aria-labelledby="upgrade-beam-heading"
      data-sticky-sentinel={isTrigger('beam') ? 'comparison-start' : undefined}
    >
      <div className="ab-cmp-inner">
        <h2 id="upgrade-beam-heading">{editorial.beamHeading}</h2>
        <p className="ab-upg-section-copy">{editorial.beamCopy}</p>
        <div className="ab-upg-beam-grid" data-testid="upgrade-beam">
          <UpgradeBeamGraphic
            label="Forward"
            mode="forward"
            ownedSweep={facts.ownedForwardSweep}
            consideredSweep={facts.consideredForwardSweep}
            interpretation={editorial.beamConclusions.forward}
            consideredLabel={considered.shortLabel}
            consideredModel={editorial.beamModelId}
            testId="upgrade-beam-forward"
          />
          <UpgradeBeamGraphic
            label="Down"
            mode="down"
            ownedSweep={facts.ownedDownSweep}
            consideredSweep={facts.consideredDownSweep}
            interpretation={editorial.beamConclusions.down}
            consideredLabel={considered.shortLabel}
            consideredModel={editorial.beamModelId}
            testId="upgrade-beam-down"
          />
          <UpgradeBeamGraphic
            label="Perspective"
            mode="perspective"
            ownedSweep={facts.ownedDownSweep}
            consideredSweep={facts.consideredDownSweep}
            interpretation={editorial.beamConclusions.perspective}
            consideredLabel={considered.shortLabel}
            consideredModel={editorial.beamModelId}
            testId="upgrade-beam-perspective"
          />
        </div>
      </div>
    </section>
  ) : null;

  const sepSection = (
    <section
      ref={isTrigger('sep') ? comparisonStartRef : undefined}
      className="ab-upg-section ab-upg-section-light"
      aria-labelledby="upgrade-sep-heading"
      data-sticky-sentinel={isTrigger('sep') ? 'comparison-start' : undefined}
    >
      <div className="ab-cmp-inner">
        <h2 id="upgrade-sep-heading">{editorial.sepHeading}</h2>
        <p className="ab-upg-section-copy" data-testid="upgrade-sep-frame">
          {editorial.sepIntro}
        </p>
        <UpgradeSeparationGraphic editorial={editorial} />
      </div>
    </section>
  );

  const rangeSection = showRange ? (
    <section
      ref={isTrigger('range') ? comparisonStartRef : undefined}
      className="ab-upg-section ab-upg-section-light"
      aria-labelledby="upgrade-range-heading"
      data-sticky-sentinel={isTrigger('range') ? 'comparison-start' : undefined}
    >
      <div className="ab-cmp-inner">
        <h2 id="upgrade-range-heading">{editorial.rangeHeading}</h2>
        <p className="ab-upg-section-copy">{editorial.rangeCopy}</p>
        <UpgradeRangeGraphic
          ownedMaxFt={facts.ownedMaxFt}
          consideredMaxFt={facts.consideredMaxFt}
          ownedLabel={owned.shortLabel}
          consideredLabel={considered.shortLabel}
          a11y={editorial.rangeA11y}
          rangeGain={editorial.rangeGain}
        />
        <p className="ab-upg-outcome" data-testid="upgrade-range-outcome">
          {editorial.rangeOutcome}
        </p>
      </div>
    </section>
  ) : null;

  const hardwareSection = (
    <section
      ref={isTrigger('hardware') ? comparisonStartRef : undefined}
      className="ab-upg-section ab-upg-section-light"
      aria-labelledby="upgrade-hw-heading"
      data-sticky-sentinel={isTrigger('hardware') ? 'comparison-start' : undefined}
    >
      <div className="ab-cmp-inner">
        <h2 id="upgrade-hw-heading">One less box to carry</h2>
        <p className="ab-upg-section-copy">{editorial.hardwareCopy}</p>
        <div className="ab-upg-hw" data-testid="upgrade-hardware">
          <article className="ab-upg-hw-path" data-testid="upgrade-hw-owned">
            <p className="ab-upg-path-kicker">LVS34</p>
            <div className="ab-upg-hw-pair" data-testid="upgrade-hw-plus">
              <span className="ab-upg-hw-photo ab-upg-hw-photo-lvs34">
                <img
                  src={LVS34_TRANSDUCER_ONLY_SRC}
                  alt="Garmin LVS34 transducer"
                  data-testid="upgrade-hw-lvs34"
                />
              </span>
              <span className="ab-upg-hw-plus" aria-hidden="true">
                +
              </span>
              <img
                className="ab-upg-hw-photo ab-upg-hw-photo-gls"
                src={owned.imageFallback}
                alt="Garmin GLS 10 black box"
                data-testid="upgrade-hw-gls"
              />
            </div>
            <p className="ab-upg-hw-status">GLS 10 required</p>
            {showWeight ? (
              <div data-testid="upgrade-weight-owned">
                <p className="ab-upg-hw-weight">{facts.publishedComponentLabel}</p>
                <p className="ab-upg-hw-breakdown">
                  {facts.ownedWeightLabel} LVS34 + {facts.glsWeightLabel} GLS 10
                </p>
              </div>
            ) : null}
          </article>
          <article className="ab-upg-hw-path" data-testid="upgrade-hw-considered">
            <p className="ab-upg-path-kicker">{considered.shortLabel}</p>
            <div className="ab-upg-hw-solo">
              <img
                className={`ab-upg-hw-photo ${editorial.hardwareImageClass}`}
                src={considered.imageFallback}
                alt={editorial.hardwareImageAlt}
                data-testid={editorial.hardwareImageTestId}
              />
            </div>
            <p className="ab-upg-hw-status" data-testid="upgrade-hw-none">
              No GLS 10 required
            </p>
            {showWeight ? (
              <div data-testid="upgrade-weight-considered">
                <p className="ab-upg-hw-weight">{facts.consideredWeightLabel}</p>
                {editorial.showIceWeight && facts.iceWeightLabel ? (
                  <p className="ab-upg-hw-ice" data-testid="upgrade-hw-ice">
                    Ice version
                    <span>
                      LVS42HD-IF {facts.iceWeightLabel}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : null}
          </article>
        </div>
        <p className="ab-upg-hw-takeaway" data-testid="upgrade-hw-takeaway">
          {editorial.hardwareTakeaway}
        </p>
      </div>
    </section>
  );

  const comparisonSections = {
    beam: beamSection,
    sep: sepSection,
    range: rangeSection,
    hardware: hardwareSection,
  };

  return (
    <div className="ab-cmp ab-upg" data-testid="upgrade-comparison">
      <header className="ab-upg-hero" data-testid="upgrade-hero">
        <div className="ab-cmp-inner ab-upg-hero-inner" data-testid="upgrade-hero-inner">
          <div className="ab-upg-hero-copy">
            <h1 className="ab-upg-title">{config.pageTitle}</h1>
            <p className="ab-upg-lede">{config.supportingLine}</p>
          </div>
          <div className="ab-upg-owner-row" data-testid="upgrade-owner-context">
            <figure className="ab-upg-owner-card">
              <p className="ab-upg-kicker">{config.ownedLabel}</p>
              <ProductImage
                product={null}
                shortLabel={owned.shortLabel}
                fallbackSrc={owned.imageFallback}
              />
              <figcaption>{owned.shortLabel}</figcaption>
            </figure>
            <figure className="ab-upg-owner-card">
              <p className="ab-upg-kicker">{config.consideringLabel}</p>
              <ProductImage
                product={consideredSide.product}
                shortLabel={considered.shortLabel}
                fallbackSrc={considered.imageFallback}
              />
              <figcaption>{considered.shortLabel}</figcaption>
            </figure>
          </div>
        </div>
      </header>

      <section className="ab-upg-verdict" data-testid="upgrade-verdict" aria-labelledby="upgrade-short-answer">
        <div className="ab-cmp-inner">
          <h2 id="upgrade-short-answer">The short answer</h2>
          <p className="ab-upg-verdict-lead" data-testid="upgrade-not-automatic">
            {editorial.notAutomatic}
          </p>
          <div className="ab-upg-paths">
            <article data-testid="keep-path">
              <p className="ab-upg-path-kicker">{editorial.keepHeading}</p>
              <ul>
                {editorial.keepItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article data-testid="upgrade-path">
              <p className="ab-upg-path-kicker">{editorial.upgradeHeading}</p>
              <ul>
                {editorial.upgradeItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <a
                className="ab-upg-price-jump"
                href="#current-prices"
                data-testid="upgrade-price-jump"
                onClick={() => {
                  trackComparisonPriceJump({
                    pageSource: AFFILIATE_PAGE_SOURCE.UPGRADE_COMPARISON,
                    comparisonId: config.id,
                    placement: 'short_answer',
                  });
                }}
              >
                {editorial.priceJumpLabel}
              </a>
            </article>
          </div>
          {editorial.alternatePath ? (
            <p className="ab-upg-alt-path" data-testid="upgrade-alt-path">
              {editorial.alternatePath.before}
              <Link to={editorial.alternatePath.href}>{editorial.alternatePath.label}</Link>
              {editorial.alternatePath.after}
            </p>
          ) : null}
        </div>
      </section>

      <section className="ab-cmp-compat-wrap" data-testid="upgrade-compat-section">
        <div className="ab-cmp-inner">
          <aside className="ab-cmp-compat" data-testid="upgrade-compatibility">
            <h2 className="ab-cmp-compat-title">{config.compatibilityHeading}</h2>
            <p>{config.compatibilityLine}</p>
            <Link
              className="ab-cmp-secondary"
              to={config.compatibilityPath}
              data-testid="upgrade-check-compatibility"
              onClick={() => {
                trackPrimaryCtaClick({
                  pageSource: AFFILIATE_PAGE_SOURCE.UPGRADE_COMPARISON,
                  placement: 'compatibility_callout',
                  cta: 'check_your_screen',
                  comparisonId: config.id,
                });
              }}
            >
              {config.compatibilityCta || 'Check Your Screen'}
            </Link>
          </aside>
        </div>
      </section>

      {editorial.sectionOrder.map((id) => (
        <React.Fragment key={id}>{comparisonSections[id]}</React.Fragment>
      ))}

      <section className="ab-cmp-more" data-testid="upgrade-specs-faq">
        <div className="ab-cmp-inner">
          <h2>Specs &amp; FAQs</h2>
          <UpgradeFaqPanels
            faqs={editorial.faqs}
            config={config}
            facts={facts}
            specRows={specRows}
            sources={sources}
            conditionNote={conditionNote}
            consideredLabel={considered.shortLabel}
          />
        </div>
      </section>

      <section
        ref={pricesRef}
        id="current-prices"
        className="ab-cmp-commerce"
        aria-labelledby="upgrade-offers-heading"
        data-testid="upgrade-current-prices"
      >
        <div className="ab-cmp-inner">
          <h2 id="upgrade-offers-heading">Decided to upgrade?</h2>
          <CompactCurrentOffer
            productConfig={considered}
            side={consideredSide}
            comparisonId={config.id}
          />
          <p className="ab-upg-offer-caveat" data-testid="upgrade-price-caveat">
            If your current Garmin screen is not compatible, the transducer price is not your
            full upgrade cost.
          </p>
          <p className="ab-cmp-disclosure" data-testid="affiliate-disclosure">
            {config.affiliateDisclosure || AFFILIATE_DISCLOSURE}
          </p>
        </div>
      </section>
      <UpgradeStickyPriceBar
        module={stickyModule}
        productLabel={considered.shortLabel}
        comparisonId={config.id}
        triggerRef={comparisonStartRef}
        pricesRef={pricesRef}
      />
    </div>
  );
}
